/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {computedFn, React} from '@api'
import {TextShapeComponent, TextShapeComponentStore} from './'
import {Utils, TLBounds} from '@tldraw/core'
import {intersectLineSegmentBounds} from '@tldraw/intersect'
import Vec from '@tldraw/vec'
import {createDefaultTextStyle, getTextSvgElement, transformRectangle, transformSingleRectangle, getBoundsRectangle} from '@modules/whiteboard'
import {TDShapeUtil} from '@modules/whiteboard/shapes/TDShapeUtil'
import {useObserver} from 'mobx-react-lite'
import {TextShape} from './'

type T = TextShape
type E = HTMLDivElement

export class TextUtil extends TDShapeUtil<T, E> {
  type = 'text' as const

  isAspectRatioLocked = false

  canEdit = true

  canBind = true

  canClone = true

  getShape = (props: Partial<T>): T => {
    return Utils.deepMerge<T>(
      {
        id: 'id',
        type: 'text',
        name: 'Text',
        parentId: 'page',
        childIndex: 1,
        point: [0, 0],
        size: [100, 100],
        rotation: 0,
        text: '',
        style: createDefaultTextStyle()
      },
      props
    )
  }

  Component = TextShapeComponent as any

  Indicator = TDShapeUtil.Indicator<T>(({shape}) => {
    return useObserver(() => {
      const {width, height} = this.getBounds(shape)
      return <rect x={0} y={0} width={width} height={height}/>
    })
  })

  // getBounds = (shape: T) => {
  //   const bounds = () => {
  //     if (!melm) {
  //       // We're in SSR
  //       console.log(`In SSR`)
  //       return {minX: 0, minY: 0, maxX: 10, maxY: 10, width: 10, height: 10}
  //     }
  //
  //     melm.innerHTML = `${shape.text}&zwj;`
  //
  //     const {tldraw: {shapeComponentStores}} = this,
  //       store = shapeComponentStores[shape.id] as TextShapeComponentStore
  //
  //     if (store) {
  //       _.assign(melm.style, store.cssStyle)
  //     }
  //
  //     // In tests, offsetWidth and offsetHeight will be 0
  //     const width = melm.offsetWidth || 1
  //     const height = melm.offsetHeight || 1
  //
  //     return {
  //       minX: 0,
  //       maxX: width,
  //       minY: 0,
  //       maxY: height,
  //       width,
  //       height
  //     }
  //   }
  //
  //   return Utils.translateBounds(bounds(), shape.point)
  // }

  shouldRender = (prev: T, next: T): boolean => {
    return (
      next.text !== prev.text || next.style.scale !== prev.style.scale || next.style !== prev.style
    )
  }

  transform = transformRectangle

  transformSingle = transformSingleRectangle

  getBounds = (shape: T) => {
    return getBoundsRectangle(shape)
  }

  getCenter = computedFn((shape: T) => {
    return Utils.getBoundsCenter(this.getBounds(shape))
  })

  hitTestPoint = computedFn((shape: T, point: number[]) => {
    return Utils.pointInBounds(point, this.getBounds(shape))
  })

  hitTestLineSegment = computedFn((shape: T, A: number[], B: number[]) => {
    return intersectLineSegmentBounds(A, B, this.getBounds(shape)).length > 0
  })

  /*
  transform = (
    shape: T,
    bounds: TLBounds,
    {initialShape, scaleX, scaleY}: TransformInfo<T>
  ): Partial<T> => {
    const {
      rotation = 0,
      style: {scale = 1}
    } = initialShape

    const nextScale = scale * Math.abs(Math.min(scaleX, scaleY))

    return {
      point: [bounds.minX, bounds.minY],
      rotation:
        (scaleX < 0 && scaleY >= 0) || (scaleY < 0 && scaleX >= 0) ? -(rotation || 0) : rotation,
      style: {
        ...initialShape.style,
        scale: nextScale
      }
    }
  }

  transformSingle = (
    shape: T,
    bounds: TLBounds,
    {initialShape, scaleX, scaleY}: TransformInfo<T>
  ): Partial<T> | void => {
    const {
      style: {scale = 1}
    } = initialShape

    return {
      point: Vec.toFixed([bounds.minX, bounds.minY]),
      style: {
        ...initialShape.style,
        scale: scale * Math.max(Math.abs(scaleY), Math.abs(scaleX))
      }
    }
  }*/

  onDoubleClickBoundsHandle = (shape: T) => {
    const center = this.getCenter(shape)

    const newCenter = this.getCenter({
      ...shape,
      style: {
        ...shape.style,
        scale: 1
      }
    })

    return {
      style: {
        ...shape.style,
        scale: 1
      },
      point: Vec.toFixed(Vec.add(shape.point, Vec.sub(center, newCenter)))
    }
  }

  getSvgElement = (shape: T): SVGElement | void => {
    const bounds = this.getBounds(shape),
      elm = getTextSvgElement(shape, bounds)

    elm.setAttribute('fill', shape.style.textColor)
    return elm
  }
}

/* -------------------------------------------------- */
/*                       Helpers                      */
/* -------------------------------------------------- */

const LETTER_SPACING = -1.5

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let melm: any

function getMeasurementDiv() {
  // A div used for measurement
  document.getElementById('__textMeasure')?.remove()

  const pre = document.createElement('pre')
  pre.id = '__textMeasure'

  Object.assign(pre.style, {
    whiteSpace: 'pre',
    width: 'auto',
    border: '1px solid transparent',
    padding: '4px',
    margin: '0px',
    letterSpacing: `${LETTER_SPACING}px`,
    opacity: '0',
    position: 'absolute',
    top: '-500px',
    left: '0px',
    zIndex: '9999',
    pointerEvents: 'none',
    userSelect: 'none',
    alignmentBaseline: 'mathematical',
    dominantBaseline: 'mathematical'
  })

  pre.tabIndex = -1

  document.body.appendChild(pre)
  return pre
}

if (typeof window !== 'undefined') {
  melm = getMeasurementDiv()
}



// WEBPACK FOOTER //
// ./src/modules/whiteboard/shapes/text/TextUtil.tsx